import {
  AnchorButton,
  Button,
  Callout,
  Hotkey,
  Hotkeys,
  HotkeysTarget
} from "@blueprintjs/core";
import * as React from "react";
import { DeleteReason } from "~/api/types";
import ProductImageList from "~/components/ProductImageModeration/ProductImageList";
import ActivityContainer from "~/containers/ActivityContainer";
import { ErrorCode } from "~/containers/ProductImageModerationFormContainer";
import {
  IAliExpressPullingImage,
  IProduct
} from "~/graphql/queries/aliexpress/optionPendingModeration";
import MainLayout from "~/layouts/MainLayout";
import styled from "~/styled";
import ProblemReporterButton from "../ProblemReporterButton";

const ButtonWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-items: center;
  justify-content: space-between;
  gap: 12px;
`;

interface IProps {
  errorCode: ErrorCode | null;

  product: IProduct;
  images: IAliExpressPullingImage[];
  color: string | null;

  discardImageIds: string[];
  mainImageId: string | null;

  onImageSelect: (imageId: string) => any;
  onImageDiscard: (imageId: string, bulk?: boolean) => any;

  onSubmit?: () => any;
  onDelete?: (reason: DeleteReason) => () => any;
  onSkip?: () => any;
}

@HotkeysTarget
export default class ProductImageModerationForm extends React.Component<
  IProps
> {
  public render() {
    const {
      errorCode,
      images,
      product,
      color,

      mainImageId,
      discardImageIds,

      onImageDiscard,
      onImageSelect,

      onSkip,
      onSubmit = () => null,
      onDelete = () => () => null
    } = this.props;

    const title = (
      <div>
        <h1>{product.sourceName}</h1>
        {color && <h2>Color: {color}</h2>}
      </div>
    );

    const actions = (
      <ButtonWrapper>
        <ActivityContainer />
        <ProblemReporterButton />
        <Button large={true} onClick={onSkip}>
          Skip
        </Button>
        <AnchorButton
          large={true}
          intent="warning"
          href={product.sourceUrl!}
          rightIcon="share"
          target="_blank"
          text="Open on AliExpress"
        />
        <Button
          large={true}
          onClick={onDelete(DeleteReason.DEFAULT)}
          intent="danger"
        >
          Delete product
        </Button>
        <Button
          large={true}
          onClick={onDelete(DeleteReason.LEGAL_TO_SELL)}
          intent="danger"
        >
          Legal to sell
        </Button>
        <Button
          large={true}
          onClick={onSubmit}
          rightIcon="arrow-right"
          intent="success"
        >
          Save and next product
        </Button>
      </ButtonWrapper>
    );

    return (
      <MainLayout title={title} actions={actions}>
        {this.renderError(errorCode)}
        <ProductImageList
          images={images}
          mainImageId={mainImageId}
          discardImageIds={discardImageIds}
          onImageDiscard={onImageDiscard}
          onImageSelect={onImageSelect}
        />
      </MainLayout>
    );
  }

  public renderHotkeys() {
    const {
      product,
      images,

      onImageDiscard,
      onImageSelect,

      onSkip,
      onSubmit = () => null,
      onDelete = () => () => null
    } = this.props;

    const openAliExpress = () =>
      product.sourceUrl && window.open(product.sourceUrl);
    const handleImageSelect = (image: IAliExpressPullingImage) => () =>
      onImageSelect(image.id);
    const handleImageDiscard = (image: IAliExpressPullingImage) => () =>
      onImageDiscard(image.id);
    const selectKeys = "1234567890qwertyuiopasdfghjklzxcvbnm".split("");

    return (
      <Hotkeys tabIndex={-1}>
        <Hotkey
          group="Global shortkeys"
          global={true}
          combo="enter"
          label="Save and next product"
          onKeyDown={onSubmit}
        />
        <Hotkey
          group="Global shortkeys"
          global={true}
          combo="right"
          label="Skip product"
          onKeyDown={onSkip}
        />
        <Hotkey
          group="Global shortkeys"
          global={true}
          preventDefault={true}
          combo="backspace"
          label="Delete product"
          onKeyDown={onDelete(DeleteReason.DEFAULT)}
        />
        <Hotkey
          group="Global shortkeys"
          global={true}
          combo="shift + backspace"
          label="Delete product (legal to sell)"
          onKeyDown={onDelete(DeleteReason.LEGAL_TO_SELL)}
        />
        <Hotkey
          group="Global shortkeys"
          global={true}
          combo="space"
          label="Open on AliExpress"
          onKeyDown={openAliExpress}
        />

        {images.slice(0, selectKeys.length).map((image, index) => (
          <Hotkey
            key={`select-${image.id}`}
            group="Selection hotkeys"
            global={true}
            combo={selectKeys[index]}
            label={`Select image ${index + 1} as main image`}
            onKeyDown={handleImageSelect(image)}
          />
        ))}

        {images.slice(0, selectKeys.length).map((image, index) => (
          <Hotkey
            key={`discard-${image.id}`}
            group="Discard hotkeys"
            global={true}
            combo={`shift + ${selectKeys[index]}`}
            label={`Discard image ${index + 1}`}
            onKeyDown={handleImageDiscard(image)}
          />
        ))}
      </Hotkeys>
    );
  }

  private renderError(errorCode: ErrorCode | null): React.ReactNode {
    switch (errorCode) {
      case ErrorCode.MAIN_IMAGE_ID_MISSING:
        return (
          <Callout intent="danger">
            You need to select a main image before saving the image. If no
            suitable image exists you can delete the product.
          </Callout>
        );

      default:
        return null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/ProductImageModeration/ProductImageModerationForm.tsx